import { useCallback } from 'react';
import { log } from 'log';
import { prop } from 'ramda';
import { useGetURLTemplate } from 'server';
import { useLocation } from 'react-router-dom';
import { getBaseDomain } from 'js/utils/url.util';
import { languageService } from 'js/services/language.service';
import { useUserSelector, useDeviceSelector } from 'js/store/hooks';
import { useGetTemporaryAuthenticationToken } from 'js/hooks/auth/hooks/useGetTemporaryAuthenticationToken';
import { useComposePlayerTagsForThunderbyteUrl } from '@interlayer/js/app/hooks/useComposePlayerTagsForThunderbyteUrl';

import * as Types from '../types';
import { useGetGameConfig } from './useGetGameConfig';
import { useGetUrlTypeList } from './useGetUrlTypeList';

// Buzz override: add support player tags in url (line 10, 25, 58), change import for useGetTemporaryAuthenticationToken (line 9)
export const useGetRedirectLaunchUrl = () => {
    const getURLTemplate = useGetURLTemplate();
    const { search } = useLocation();
    const lang = useUserSelector(prop('lang'));
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const getTemporaryAuthenticationToken = useGetTemporaryAuthenticationToken();
    const getUrlTypeList = useGetUrlTypeList();
    const getGameConfig = useGetGameConfig();
    const composePlayerTagsForThunderbyteUrl = useComposePlayerTagsForThunderbyteUrl();

    return useCallback(async (
        { gameCode, mode, additionalQueryString, clientType, customParameters }: Types.IGetLaunchURLParams
    ): Promise<string> => {
        const currentSearchParams = new URLSearchParams(search);
        const gameConfig = getGameConfig(gameCode);
        const urlTypeList = getUrlTypeList({ gameCode, mode });

        if (!gameConfig) {
            return '';
        }

        let launchUrlStr = await getURLTemplate({
            urlTypeList,
            clientPlatform: isDesktop ? 'web' : 'mobile',
            clientType,
            customParameters
        });

        if (!launchUrlStr) {
            throw new Error(`Launcher URL type ${urlTypeList[0]} is not configured on IMS`);
        }

        launchUrlStr = launchUrlStr
            .replace('[superdomain]', getBaseDomain())
            .replace(/\[gamename]/g, gameConfig.name)
            .replace(/\[gamecode]/g, gameConfig.gameCode)
            .replace(/\[mobilegamecode]/g, gameConfig.mobileGameCode || gameConfig.gameCode)
            .replace(/\[integrationtype]/g, gameConfig.integrationType || '')
            .replace(/\[livelaunchalias]/g, gameConfig.liveLaunchAlias || '')
            .replace(/\[language]/g, languageService.getTwoLetterLanguage(lang))
            .replace(/\[back_url]/g, window.location.origin)
            .replace(/\[playertags:\S*\]/, composePlayerTagsForThunderbyteUrl(launchUrlStr));

        if (launchUrlStr.includes('[temptoken]')) {
            const token = await getTemporaryAuthenticationToken();

            launchUrlStr = launchUrlStr.replace(/\[temptoken]/g, token);
        }

        const launchUrl = new window.URL(launchUrlStr);

        // Query string from CMS game config
        if (gameConfig.urlCustomParameters) {
            try {
                const additionalUrlCustomParameters = new window.URLSearchParams(gameConfig.urlCustomParameters);

                additionalUrlCustomParameters.forEach((value, key) => {
                    launchUrl.searchParams.set(key, value);
                });
            } catch {
                log('error', `Game configuration error: check urlCustomParameters for game "${gameConfig.name}"`);
            }
        }

        // Custom URL params added in URL bar
        currentSearchParams.forEach((value, key) => {
            if (!['real', 'back_url'].includes(key)) {
                launchUrl.searchParams.set(key, value);
            }
        });

        // Games portlet query string
        if (additionalQueryString) {
            try {
                const additionalQueryParams = new URLSearchParams(additionalQueryString);

                additionalQueryParams.forEach((value, key) => {
                    launchUrl.searchParams.set(key, value);
                });
            } catch {
                log('error', `Cannot parse additionalQueryString for game "${gameConfig.name}"`);
            }
        }

        return launchUrl.toString();
    }, [
        lang,
        search,
        isDesktop,
        getGameConfig,
        getURLTemplate,
        getUrlTypeList,
        getTemporaryAuthenticationToken,
        composePlayerTagsForThunderbyteUrl
    ]);
};
